import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProxyService } from './proxy.service';
import { ProxyFormDialog } from './proxy-form-dialog/proxy-form-dialog';
import { ConfirmDialog } from '../shared/confirm-dialog/confirm-dialog';
import { Header } from '../shared/header/header';
import { PROXY_ALERT_GB, ProxyModel, ProxyUsage } from './proxy.type';
import { AuthService } from '../auth/auth.service';

@Component({
  selector: 'app-proxy',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSnackBarModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatPaginatorModule,
    MatProgressBarModule,
    Header,
  ],
  templateUrl: './proxy.html',
  styleUrl: './proxy.scss'
})
export class Proxy {
  private proxyService = inject(ProxyService);
  readonly canEdit = inject(AuthService).canWrite('system');
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);


  proxies: ProxyModel[] = [];
  displayedColumns: string[] = [
    'select',
    'address',
    'port',
    'username',
    'active',
    'actions',
  ];

  loading: boolean = false;
  usage: ProxyUsage | null = null;
  total: number = 0;
  pageIndex: number = 0;
  pageSize: number = 50;
  selectedProxyIds = new Set<string>();
  filterSearch: string = '';
  private searchSubject = new Subject<string>();
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.pageIndex = 0;
      this.loadProxies();
    });
    this.loadProxies();
    this.proxyService.getProxyUsage().subscribe({
      next: usage => this.usage = usage,
      error: () => this.usage = null,
    });
  }

  onSearchChange(): void {
    this.searchSubject.next(this.filterSearch);
  }

  loadProxies(): void {
    this.loading = true;

    this.proxyService.getProxies({
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      search: this.filterSearch || undefined,
    }).subscribe({
      next: (response) => {
        this.proxies = response.items || [];
        this.total = response.total;
        this.pageIndex = response.pageIndex;
        this.pageSize = response.pageSize;
        this.selectedProxyIds.clear();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading proxies:', error);
        this.snackBar.open('Error loading proxies', 'Close', {
          duration: 3000,
        });
        this.loading = false;
      },
    });
  }

  onPage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.loadProxies();
  }

  get usageLow(): boolean {
    return !!this.usage && (this.usage.low_balance || this.usage.gb_remaining <= PROXY_ALERT_GB);
  }

  get selectedCount(): number {
    return this.selectedProxyIds.size;
  }

  isSelected(proxy: ProxyModel): boolean {
    return this.selectedProxyIds.has(proxy.id);
  }

  isAllSelected(): boolean {
    return this.proxies.length > 0 && this.proxies.every(proxy => this.selectedProxyIds.has(proxy.id));
  }

  isPartiallySelected(): boolean {
    return this.selectedProxyIds.size > 0 && !this.isAllSelected();
  }

  toggleSelection(proxy: ProxyModel, checked: boolean): void {
    if (checked) {
      this.selectedProxyIds.add(proxy.id);
    } else {
      this.selectedProxyIds.delete(proxy.id);
    }
  }

  toggleAll(checked: boolean): void {
    if (checked) {
      this.proxies.forEach(proxy => this.selectedProxyIds.add(proxy.id));
      return;
    }

    this.selectedProxyIds.clear();
  }

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(ProxyFormDialog, {
      width: '500px',
      disableClose: true,
      data: {
        mode: 'create',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadProxies();
      }
    });
  }

  openEditDialog(proxy: ProxyModel): void {
    const dialogRef = this.dialog.open(ProxyFormDialog, {
      width: '500px',
      disableClose: true,
      data: {
        mode: 'edit',
        proxy: proxy,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadProxies();
      }
    });
  }

  toggleActive(proxy: ProxyModel): void {
    const newActiveStatus = !proxy.active;

    this.proxyService.updateProxyActive(proxy.id, newActiveStatus).subscribe({
      next: () => {
        this.snackBar.open(
          `Proxy ${newActiveStatus ? 'activated' : 'deactivated'} successfully`,
          'Close',
          { duration: 3000 }
        );
        this.loadProxies();
      },
      error: (error) => {
        console.error('Error toggling proxy active status:', error);
        this.snackBar.open('Error updating proxy status', 'Close', {
          duration: 3000,
        });
      },
    });
  }

  bulkSetActive(active: boolean): void {
    const ids = Array.from(this.selectedProxyIds);

    if (!ids.length) {
      return;
    }

    this.proxyService.updateProxiesActive(ids, active).subscribe({
      next: (response) => {
        this.snackBar.open(
          `${response.affected} proxies ${active ? 'activated' : 'deactivated'} successfully`,
          'Close',
          { duration: 3000 }
        );
        this.loadProxies();
      },
      error: (error) => {
        console.error('Error updating proxies active status:', error);
        this.snackBar.open('Error updating proxy statuses', 'Close', {
          duration: 3000,
        });
      },
    });
  }

  bulkDelete(): void {
    const ids = Array.from(this.selectedProxyIds);

    if (!ids.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialog, {
      width: '400px',
      data: {
        title: 'Delete Proxies',
        message: `Are you sure you want to delete ${ids.length} selected proxies? This action cannot be undone.`,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.proxyService.deleteProxies(ids).subscribe({
          next: (response) => {
            this.snackBar.open(`${response.affected} proxies deleted successfully`, 'Close', {
              duration: 3000,
            });
            this.loadProxies();
          },
          error: (error) => {
            console.error('Error deleting proxies:', error);
            this.snackBar.open('Error deleting proxies', 'Close', {
              duration: 3000,
            });
          },
        });
      }
    });
  }

  deleteProxy(proxy: ProxyModel): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      width: '400px',
      data: {
        title: 'Delete Proxy',
        message: `Are you sure you want to delete proxy "${proxy.address}"? This action cannot be undone.`,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.proxyService.deleteProxy(proxy.id).subscribe({
          next: () => {
            this.snackBar.open('Proxy deleted successfully', 'Close', {
              duration: 3000,
            });
            this.loadProxies();
          },
          error: (error) => {
            console.error('Error deleting proxy:', error);
            this.snackBar.open('Error deleting proxy', 'Close', {
              duration: 3000,
            });
          },
        });
      }
    });
  }
}
